import React, { useMemo, useState, useEffect } from "react";
import TeamPage from "./TeamPage";
import WhereHowSection from "./components/WhereHowSection";
import WhyChooseScroller from "./components/WhyChooseScroller";
import SpotlightSection from "./components/SpotlightSection";
import WhyChooseUsPage from "./WhyChooseUsPage";
import logo from "./assets/rex-counsel-logo.png";

/**
 * Rex Counsel LLP — Single‑file React site
 * Styling: Tailwind CSS (v3)
 * How to edit content:
 *  - Update the data objects (TEAM, PRACTICE_AREAS, HIGHLIGHTS, CONTACT_INFO) below.
 *  - Colors are centralized in COLORS.
 *  - Each section is a small component; reorder or remove as you like.
 */

// ===== Design Tokens =====
const COLORS = {
  primary: "#2B4C65",   // Deep blue-grey
  accent: "#6B8BA4",    // Muted steel blue
  pale: "#95B4CC",      // Light desaturated blue
  ink: "#1D1C22",       // Near-black
  white: "#FFFFFF",
};

// ===== Editable Content =====
const CONTACT_INFO = {
  email: "contact@rexcounsel.com",
  phone: "+91 98995 37377 / +44 7721 874713 ",
  address: "2A The Common, Southall UB2 5PJ, United Kingdom",
  addressIN: "56/40,Sahibabad Industrial Area Site 4, Ghaziabad"
  
};

const PRACTICE_AREAS = [
  { title: "Corporate & Commercial", blurb: "Company formation, contracts, compliance, due diligence, and M&A advisory.", icon: "🏢" },
  { title: "Civil Litigation", blurb: "Property, contractual disputes, injunctions, and appellate strategy.", icon: "⚖️" },
  { title: "Criminal Law", blurb: "White-collar defense, cyber offenses, bail, and trial support.", icon: "🛡️" },
  { title: "Family & Matrimonial", blurb: "Mediation-first approach for matrimonial, custody, and maintenance matters.", icon: "👨‍👩‍👧" },
  { title: "Immigration", blurb: "Work permits, OCI/PIO, and cross-border advisory.", icon: "🌏" },
  { title: "Intellectual Property", blurb: "Trademarks, copyrights, licensing, and enforcement actions.", icon: "🧠" },
  { title: "Cyber & Data", blurb: "Data privacy, cyber forensics, audits, and incident response.", icon: "💾" },
  { title: "Arbitration", blurb: "Domestic & international arbitration, enforcement of awards.", icon: "📜" },
];

const TEAM = [
  {
    name: "Adv. Aakarsh Tomar",
    role: "Director & Founding Partner",
    focus: ["Corporate", "Arbitration", "IP"],
    bio: "Strategic counsel for growth-stage businesses; leads cross-border transactions and complex arbitrations.",
    img: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?q=80&w=800&auto=format&fit=crop",
    email: "aakarsh@rexcounsel.com",
  },
  {
    name: "Adv. [Director 2]",
    role: "Director",
    focus: ["Criminal", "Cyber"],
    bio: "Defence-first mindset with experience across cyber-crime and white-collar investigations.",
    img: "https://images.unsplash.com/photo-1547425260-76bcadfb4f2c?q=80&w=800&auto=format&fit=crop",
    email: "director2@rexcounsel.com",
  },
  {
    name: "Adv. [Director 3]",
    role: "Director",
    focus: ["Civil", "Family"],
    bio: "Client-first advocate specialising in high-stakes civil litigation and sensitive family matters.",
    img: "https://images.unsplash.com/photo-1548142813-c348350df52b?q=80&w=800&auto=format&fit=crop",
    email: "director3@rexcounsel.com",
  },
];

const HIGHLIGHTS = [
  { k: "Experience", v: "4+ Years" },
  { k: "International Services", v: "Available" },
  { k: "Unmatched", v: "Expertise" },
  { k: "Meetings", v: "Online & In‑Person" },
];

// ===== Utilities =====
const Section = ({ id, title, subtitle, children }) => (
  <section id={id} className="py-16 lg:py-24">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      {(title || subtitle) && (
        <header className="mb-10">
          {subtitle && (
            <p className="text-sm tracking-widest uppercase text-[color:var(--accent)] mb-2" style={{"--accent": COLORS.accent}}>
              {subtitle}
            </p>
          )}
          {title && (
            <h2 className="text-3xl md:text-4xl font-semibold text-[color:var(--ink)]" style={{"--ink": COLORS.ink}}>
              {title}
            </h2>
          )}
        </header>
      )}
      {children}
    </div>
  </section>
);

const Card = ({ children, className = "" }) => (
  <div
    className={
      "group relative rounded-2xl bg-white/90 border border-slate-200 p-6 shadow-sm overflow-hidden transition-transform duration-300 will-change-transform hover:-translate-y-1 hover:shadow-xl " +
      className
    }
  >
    <div className="absolute -top-24 -right-24 w-48 h-48 rounded-full opacity-10 blur-2xl" style={{ background: `radial-gradient(closest-side, ${COLORS.pale}, transparent)` }} />
    {children}
  </div>
);

const FloatingRow = ({ children }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 [animation:floatY_10s_ease-in-out_infinite]">
    {children}
    <style>{`
      @keyframes floatY { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-6px)} }
    `}</style>
  </div>
);

// ===== Sections =====
const Navbar = ({ onNavigate }) => {
    const links = [
      { key: 'practice', label: 'Practice Areas', href: '#practice' },
      { key: 'team',     label: 'Meet Our Team',  href: '/team', navigateTo: 'team' }, // toggles TeamPage
      { key: 'why',      label: 'Why Choose Us',  href: '/why',  navigateTo: 'why'  }, // toggles Why page
      
    ];
  
    return (
      <nav className="sticky top-0 z-50 backdrop-blur bg-white/70 border-b" style={{ borderColor: COLORS.pale }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <a
            href="#home"
            onClick={(e) => { e.preventDefault(); onNavigate && onNavigate('home'); }}
            className="flex items-center gap-3"
          >
            {/* ✅ Logo */}
            {/* Circle wrapper to hide any square/white canvas */}
  <div className="w-9 h-9 rounded-full overflow-hidden">
    <img
      src={logo}
      alt="Rex Counsel Logo"
      className="w-full h-full object-cover block"
    />
  </div>
            <span className="font-semibold text-lg" style={{ color: COLORS.ink }}>Rex Counsel LLP</span>
          </a>
  
          <div className="hidden md:flex items-center gap-6">
            {links.map((l) => (
              <a
                key={l.key}
                href={l.href}
                onClick={(e) => {
                  if (l.navigateTo) { e.preventDefault(); onNavigate && onNavigate(l.navigateTo); }
                }}
                className="text-sm hover:opacity-70"
                style={{ color: COLORS.ink }}
              >
                {l.label}
              </a>
            ))}
            <a
              href="#contact"
              className="px-3 py-2 rounded-xl text-sm font-medium"
              style={{ background: COLORS.primary, color: COLORS.white }}
            >
              Book a Consultation
            </a>
          </div>
        </div>
      </nav>
    );
  };
    
  

const Hero = () => (
  <section id="home" className="relative overflow-hidden">
    <div className="absolute inset-0" style={{
      background: `linear-gradient(135deg, ${COLORS.pale} 0%, ${COLORS.accent} 35%, ${COLORS.primary} 100%)`,
      opacity: 0.15,
    }} />
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 relative">
      <div className="grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <h1 className="text-4xl md:text-5xl font-semibold leading-tight mb-4" style={{ color: COLORS.ink }}>
            Clarity. Strategy. Results.
          </h1>
          <p className="text-lg md:text-xl text-slate-700 mb-8">
            Full-service legal counsel for businesses and individuals—delivered with precision and a responsive, client-first approach.
          </p>
          <div className="flex flex-wrap gap-3">
            <a href="#contact" className="px-5 py-3 rounded-xl text-sm font-medium" style={{ background: COLORS.primary, color: COLORS.white }}>Book a Consultation</a>
            <a href="#practice" className="px-5 py-3 rounded-xl text-sm font-medium border" style={{ borderColor: COLORS.accent, color: COLORS.primary }}>Explore Practice Areas</a>
          </div>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
            {HIGHLIGHTS.map((h) => (
              <div key={h.k} className="rounded-xl bg-white/70 border p-4 text-center" style={{ borderColor: COLORS.pale }}>
                <div className="text-2xl font-semibold" style={{ color: COLORS.primary }}>{h.v}</div>
                <div className="text-xs text-slate-600">{h.k}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="relative">
          <div className="aspect-[4/3] rounded-3xl border overflow-hidden shadow-lg" style={{ borderColor: COLORS.pale }}>
            <img
              alt="Rex Counsel — courtroom"
              className="w-full h-full object-cover"
              src="https://images.unsplash.com/photo-1589829545856-d10d557cf95f?q=80&w=1600&auto=format&fit=crop"
            />
          </div>
          <div className="absolute -bottom-6 -left-6 bg-white/90 rounded-2xl border p-4 shadow-md w-64" style={{ borderColor: COLORS.pale }}>
            <div className="text-sm font-medium" style={{ color: COLORS.ink }}>ISO-aligned processes</div>
            <div className="text-xs text-slate-600">Document control • Quality checks • Timely updates</div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const PracticeAreas = () => (
  <Section id="practice" subtitle="Expertise" title="Practice Areas">
    <FloatingRow>
      {PRACTICE_AREAS.map((p) => (
        <Card key={p.title}>
          <div className="flex items-start gap-4">
            <div className="text-3xl select-none" aria-hidden>{p.icon}</div>
            <div>
              <h3 className="text-lg font-semibold" style={{ color: COLORS.ink }}>{p.title}</h3>
              <p className="text-sm text-slate-600 mt-1">{p.blurb}</p>
            </div>
          </div>
        </Card>
      ))}
    </FloatingRow>
  </Section>
);

const WhyChoose = () => (
  <Section id="why" subtitle="Our Approach" title="Why Choose Rex Counsel?">
    <div className="grid md:grid-cols-3 gap-6">
      {[{
        t: "Client‑First, Always",
        d: "Clear communication, strategic roadmaps, and proactive updates at every stage.",
      }, {
        t: "Cross‑Functional Expertise",
        d: "Civil, criminal, corporate, family, immigration, IP, cyber, and arbitration under one roof.",
      }, {
        t: "Outcome‑Driven",
        d: "We align legal insight with practical solutions, optimising for risk and business impact.",
      }].map((x) => (
        <Card key={x.t}>
          <h3 className="text-lg font-semibold mb-2" style={{ color: COLORS.ink }}>{x.t}</h3>
          <p className="text-sm text-slate-600">{x.d}</p>
        </Card>
      ))}
    </div>
  </Section>
);

const Team = () => (
  <Section id="team" subtitle="People" title="Meet Our Team">
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {TEAM.map((m) => (
        <Card key={m.name}>
          <div className="flex items-center gap-4">
            <img src={m.img} alt={m.name} className="w-16 h-16 rounded-xl object-cover" />
            <div>
              <div className="font-semibold" style={{ color: COLORS.ink }}>{m.name}</div>
              <div className="text-xs text-slate-600">{m.role}</div>
            </div>
          </div>
          <p className="text-sm text-slate-700 mt-3">{m.bio}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {m.focus.map((f) => (
              <span key={f} className="text-xs px-2 py-1 rounded-lg border" style={{ borderColor: COLORS.pale, color: COLORS.primary }}>{f}</span>
            ))}
          </div>
          <a href={`mailto:${m.email}`} className="mt-4 inline-block text-sm underline hover:no-underline" style={{ color: COLORS.primary }}>Contact</a>
        </Card>
      ))}
    </div>
    <div className="mt-6 text-sm text-slate-600">
      <strong>Make it yours:</strong> update the <code>TEAM</code> array at the top—add photos, roles, bios, and focus tags.
    </div>
  </Section>
);

const Insights = () => {
  const posts = [
    { title: "What changes in India’s DPDP Act mean for SMEs", tag: "Data & Privacy", href: "#" },
    { title: "Checklist: Contracts every startup needs in year one", tag: "Corporate", href: "#" },
    { title: "When to choose arbitration over litigation", tag: "Arbitration", href: "#" },
  ];
  return (
    <Section id="insights" subtitle="Resources" title="Latest Insights">
      <div className="flex gap-6 overflow-x-auto snap-x pb-2">
        {posts.map((p) => (
          <a key={p.title} href={p.href} className="min-w-[320px] snap-start">
            <Card>
              <div className="text-xs mb-2" style={{ color: COLORS.accent }}>{p.tag}</div>
              <div className="font-medium" style={{ color: COLORS.ink }}>{p.title}</div>
              <div className="mt-3 text-sm text-slate-600">Read more →</div>
            </Card>
          </a>
        ))}
      </div>
    </Section>
  );
};

const Contact = () => {
  // ✅ Hooks must be at the top level of the component
  const [matterType, setMatterType] = useState("");

  return (
    <Section id="contact" subtitle="Start a Conversation" title="Get in Touch">
      <div className="grid lg:grid-cols-2 gap-8">
        <Card>
          <form className="space-y-4">
            {/* Row 1: Name + Email */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-slate-600">Name</label>
                <input
                  className="mt-1 w-full rounded-xl border px-3 py-2 focus:outline-none"
                  style={{ borderColor: COLORS.pale }}
                  placeholder="Your full name"
                />
              </div>
              <div>
                <label className="text-sm text-slate-600">Email</label>
                <input
                  type="email"
                  className="mt-1 w-full rounded-xl border px-3 py-2 focus:outline-none"
                  style={{ borderColor: COLORS.pale }}
                  placeholder="name@email.com"
                />
              </div>
            </div>

            {/* Row 2: Country + City/State */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-slate-600">Country</label>
                <input
                  className="mt-1 w-full rounded-xl border px-3 py-2 focus:outline-none"
                  style={{ borderColor: COLORS.pale }}
                  placeholder="India"
                />
              </div>
              <div>
                <label className="text-sm text-slate-600">City & State</label>
                <input
                  className="mt-1 w-full rounded-xl border px-3 py-2 focus:outline-none"
                  style={{ borderColor: COLORS.pale }}
                  placeholder="New Delhi, Delhi"
                />
              </div>
            </div>

            {/* Row 3: Phone Number */}
            <div>
              <label className="text-sm text-slate-600">Phone Number</label>
              <input
                type="tel"
                className="mt-1 w-full rounded-xl border px-3 py-2 focus:outline-none"
                style={{ borderColor: COLORS.pale }}
                placeholder="+91 98765 43210"
              />
            </div>

            {/* Row 4: Matter Type (select + conditional Others) */}
            <div>
              <label className="text-sm text-slate-600">Matter Type</label>
              <select
                value={matterType}
                onChange={(e) => setMatterType(e.target.value)}
                className="mt-1 w-full rounded-xl border px-3 py-2 focus:outline-none"
                style={{ borderColor: COLORS.pale }}
              >
                <option value="">Select a matter type</option>
                <option value="Corporate & Commercial">Corporate & Commercial</option>
                <option value="Civil Litigation">Civil Litigation</option>
                <option value="Criminal Law">Criminal Law</option>
                <option value="Family & Matrimonial">Family & Matrimonial</option>
                <option value="Intellectual Property">Intellectual Property</option>
                <option value="Cyber & Data">Cyber & Data</option>
                <option value="Arbitration">Arbitration</option>
                <option value="Immigration">Immigration</option>
                <option value="Others">Others</option>
              </select>

              {matterType === "Others" && (
                <input
                  className="mt-2 w-full rounded-xl border px-3 py-2 focus:outline-none"
                  style={{ borderColor: COLORS.pale }}
                  placeholder="Please specify your matter type"
                />
              )}
            </div>

            {/* Row 5: Party Type */}
            <div>
              <label className="text-sm text-slate-600 mb-2 block">Party Type</label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input type="radio" name="partyType" value="Litigant" /> Litigant
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input type="radio" name="partyType" value="Defendant" /> Defendant
                </label>
              </div>
            </div>

            {/* Row 6: Message */}
            <div>
              <label className="text-sm text-slate-600">Message</label>
              <textarea
                rows={5}
                className="mt-1 w-full rounded-xl border px-3 py-2 focus:outline-none"
                style={{ borderColor: COLORS.pale }}
                placeholder="Tell us a bit about your matter..."
              />
            </div>

            {/* Row 7: Attachment */}
            <div>
              <label className="text-sm text-slate-600">Attachment (optional)</label>
              <input
                type="file"
                className="mt-1 w-full text-sm text-slate-700 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-[color:var(--pale)] file:text-slate-800 hover:file:bg-[color:var(--accent)]"
                style={{ "--pale": COLORS.pale, "--accent": COLORS.accent }}
              />
            </div>

            {/* Submit Button */}
            <button
              type="button"
              className="px-5 py-3 rounded-xl text-sm font-medium w-full"
              style={{ background: COLORS.primary, color: COLORS.white }}
            >
              Submit
            </button>
          </form>
        </Card>

        {/* Right side: details card stays as-is */}
        <div className="space-y-4">
          <Card>
            <h3 className="font-semibold mb-2" style={{ color: COLORS.ink }}>Contact Details</h3>
            <ul className="text-sm text-slate-700 space-y-1">
              <li><strong>Email:</strong> <a href={`mailto:${CONTACT_INFO.email}`} className="underline" style={{ color: COLORS.primary }}>{CONTACT_INFO.email}</a></li>
              <li><strong>Phone:</strong> <a href={`tel:${CONTACT_INFO.phone}`} className="underline" style={{ color: COLORS.primary }}>{CONTACT_INFO.phone}</a></li>
              <li><strong>Address UK:</strong> {CONTACT_INFO.address}</li>
              <li><strong>Address IN:</strong> {CONTACT_INFO.addressIN}</li>
            </ul>
          </Card>
          <Card>
            <h3 className="font-semibold mb-2" style={{ color: COLORS.ink }}>Working Hours</h3>
            <p className="text-sm text-slate-700">Mon–Fri · 9:30 AM – 6:30 PM IST</p>
          </Card>
        </div>
      </div>
    </Section>
  );
};

const Footer = () => (
  <footer className="mt-16 border-t" style={{ borderColor: COLORS.pale }}>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 flex flex-col md:flex-row items-center justify-between gap-4">
      <div className="text-sm text-slate-600">© {new Date().getFullYear()} Rex Counsel LLP. All rights reserved.</div>
      <div className="flex items-center gap-4 text-sm">
        <a href="#" className="hover:underline" style={{ color: COLORS.primary }}>Privacy</a>
        <a href="#" className="hover:underline" style={{ color: COLORS.primary }}>Terms</a>
        <a href="#contact" className="hover:underline" style={{ color: COLORS.primary }}>Contact</a>
      </div>
    </div>
  </footer>
);

  
export default function RexCounselSite() {
    const swatches = useMemo(() => Object.entries(COLORS), []);
    const [page, setPage] = useState('home');
  
    return (
      <main className="min-h-screen" style={{ background: COLORS.white }}>
        {/* Navbar MUST receive onNavigate so it can switch views */}
        <Navbar onNavigate={setPage} />
  
        {page === 'home' ? (
          <>
            <Hero />
            <PracticeAreas />
            <SpotlightSection />
            <WhyChooseScroller />
            {/* If you want the Team preview on Home, uncomment next line */}
            {/* <Team /> */}
            
            <WhereHowSection />
            <Contact />
            <Footer />
          </>
        ) : page === 'why' ? (
          <>
            <WhyChooseUsPage onNavigate={setPage} />
            <Footer />
          </>
        ) : page === 'team' ? (
          <>
            <TeamPage
              onBack={() => setPage('home')}
              onSelect={(member) => {
                // later: navigate to /team/<slug>
                console.log('Clicked member:', member);
              }}
            />
            <Footer />
          </>
        ) : null}
  
        {/* Optional: floating palette widget */}
        <div
          className="fixed bottom-4 right-4 bg-white/90 border rounded-2xl shadow-md p-3 hidden lg:block"
          style={{ borderColor: COLORS.pale }}
        >
          <div className="text-xs font-medium mb-2" style={{ color: COLORS.ink }}>Palette</div>
          <div className="flex gap-2">
            {swatches.map(([k, v]) => (
              <div key={k} className="w-6 h-6 rounded" title={`${k}: ${v}`} style={{ background: v }} />
            ))}
          </div>
        </div>
      </main>
    );
  }
  